import { useEffect, useMemo, useState } from "react";
import type { QueueBoardRoom } from "../types";
import { useQueueFlash } from "../hooks/useQueueFlash";
import { queueFont } from "../queueFont";
import { CareCornerLogo } from "./CareCornerLogo";
import { useSideVideoLayout } from "./SideVideoLayoutContext";
import { WelcomeHeader } from "./WelcomeHeader";

/** Max doctor/room cards shown at once when fix pagination is on. */
export const QUEUE_BOARD_MAX_PER_PAGE = 8;
const PAGE_ROTATE_MS = 10000;

type RoomCardsGridProps = {
  clinicName: string;
  rooms: QueueBoardRoom[];
  /**
   * When true, show at most 8 cards per page and auto-rotate if there are more.
   * Enable via `?fix=1` (or `?fixed=1`) on the queue URL.
   */
  fixPagination?: boolean;
  /** Session from `?sessionName=`; shown once in the header instead of on every card. */
  sessionName?: string;
};

function isTruthyFlag(raw: string | null | undefined): boolean {
  const v = String(raw ?? "")
    .trim()
    .toLowerCase();
  return v === "1" || v === "true" || v === "yes" || v === "on";
}

export function parseFixPaginationFlag(query: URLSearchParams): boolean {
  return isTruthyFlag(query.get("fix")) || isTruthyFlag(query.get("fixed"));
}

/** Column layout only — card size/type scale is pure CSS via [data-count]. */
function gridColsClass(count: number, fixPagination: boolean): string {
  if (fixPagination) return "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4";
  if (count <= 1) return "grid-cols-1";
  if (count === 2) return "grid-cols-2";
  if (count === 3) return "grid-cols-3";
  if (count === 4) return "grid-cols-2 lg:grid-cols-4";
  if (count <= 6) return "grid-cols-3";
  if (count <= 8) return "grid-cols-4";
  if (count <= 9) return "grid-cols-3";
  return "grid-cols-4 xl:grid-cols-5";
}

function roomTitle(room: QueueBoardRoom): string {
  const roomNo = String(room.id ?? "").trim();
  const title = String(room.name ?? "").trim();
  const roomLabel = roomNo ? `Room ${roomNo}` : "";
  // Avoid "Room 1" twice when title is already the room label
  const titleLooksLikeRoom =
    !!roomNo &&
    (title.toLowerCase() === roomLabel.toLowerCase() ||
      title.toLowerCase() === roomNo.toLowerCase());
  return titleLooksLikeRoom ? "" : title;
}

function RoomCard({ room, hideTitle = false }: { room: QueueBoardRoom; hideTitle?: boolean }) {
  const flashing = useQueueFlash(room.currentQueue);
  const roomNo = String(room.id ?? "").trim();
  const roomLabel = roomNo ? `Room ${roomNo}` : "";
  const title = roomTitle(room);
  const showTitle = !!title && !hideTitle;
  const showRoomNo = !!roomLabel;

  return (
    <div
      data-flash={flashing ? "1" : "0"}
      className={`${queueFont} nq-card flex h-full min-h-0 w-full min-w-0 flex-col items-center border border-solid border-[#cbd5e1] bg-gradient-to-b from-slate-50 to-white text-center shadow-[0_4px_16px_rgba(15,23,42,0.06)]`}
    >
      <div className="nq-card-head flex w-full min-w-0 shrink-0 flex-col items-center">
        {showTitle ? (
          <div
            className={`${queueFont} nq-name max-w-full truncate rounded-full bg-[#e2e8f0] font-bold uppercase tracking-[0.08em] text-[#334155]`}
          >
            {title}
          </div>
        ) : null}
        {showRoomNo ? (
          <div
            className={`${queueFont} nq-room w-full break-words font-extrabold leading-none text-[#0b3a66] [overflow-wrap:anywhere]`}
          >
            {roomLabel}
          </div>
        ) : null}
        <div
          className={`${queueFont} nq-doctor w-full break-words font-bold leading-snug text-[#1e293b] [overflow-wrap:anywhere]`}
        >
          {room.doctorName}
        </div>
      </div>
      <div className="nq-card-serving flex w-full min-h-0 flex-1 flex-col items-center justify-center">
        <div className="nq-label font-bold uppercase leading-tight tracking-[0.14em] text-slate-500">
          Now serving
        </div>
        <div
          className={`${queueFont} nq-queue w-full font-extrabold leading-none text-[#0b3a66] [font-variant-numeric:tabular-nums]`}
        >
          {room.currentQueue}
        </div>
      </div>
    </div>
  );
}

/**
 * Pure CSS size scale by visible card count.
 * Fewer cards → larger type / padding; more cards → denser.
 */
const QUEUE_BOARD_COUNT_CSS = `
.nq-board { --nq-max-w: min(1800px, 96vw); --nq-max-h: 100%; --nq-u: clamp(10px, 1.3vmin, 16px); }
.nq-board[data-count="1"] { --nq-max-w: min(720px, 78vw); --nq-max-h: min(80vh, 760px); --nq-u: clamp(16px, 2.6vmin, 30px); }
.nq-board[data-count="2"] { --nq-max-w: min(1300px, 94vw); --nq-max-h: min(76vh, 700px); --nq-u: clamp(14px, 2.3vmin, 26px); }
.nq-board[data-count="3"] { --nq-max-w: min(1700px, 96vw); --nq-max-h: min(74vh, 680px); --nq-u: clamp(12px, 2.3vmin, 26px); }
.nq-board[data-count="4"] { --nq-max-w: min(1800px, 96vw); --nq-max-h: min(76vh, 700px); --nq-u: clamp(11px, 2vmin, 22px); }
.nq-board[data-count="5"],
.nq-board[data-count="6"] { --nq-u: clamp(10px, 1.8vmin, 20px); }
.nq-board[data-count="7"],
.nq-board[data-count="8"] { --nq-u: clamp(8px, 1.2vmin, 13px); }
.nq-board[data-count="9"],
.nq-board[data-count="10"],
.nq-board[data-count="11"],
.nq-board[data-count="12"] { --nq-u: clamp(7px, 1vmin, 11px); }

.nq-board .nq-grid-shell {
  width: 100%;
  max-width: var(--nq-max-w);
  max-height: var(--nq-max-h);
  height: var(--nq-max-h);
}

/* One unit (--nq-u) drives every size so the room / doctor / number ratio stays fixed. */
.nq-board .nq-card { border-radius: calc(var(--nq-u) * 0.9); padding: calc(var(--nq-u) * 1.1) calc(var(--nq-u) * 1.2) calc(var(--nq-u) * 0.9); }
.nq-board .nq-card-head { gap: calc(var(--nq-u) * 0.45); }
.nq-board .nq-name { font-size: calc(var(--nq-u) * 0.95); padding: calc(var(--nq-u) * 0.15) calc(var(--nq-u) * 0.8); margin-bottom: calc(var(--nq-u) * 0.15); }
.nq-board .nq-room { font-size: calc(var(--nq-u) * 3.2); }
.nq-board .nq-doctor { font-size: calc(var(--nq-u) * 1.7); }
.nq-board .nq-card-serving { gap: calc(var(--nq-u) * 0.3); padding-top: calc(var(--nq-u) * 0.6); }
.nq-board .nq-label { font-size: calc(var(--nq-u) * 0.95); }
.nq-board .nq-queue { font-size: calc(var(--nq-u) * 9); }

/* Flash when the served number changes so people look up. */
@keyframes nq-card-flash {
  0%, 100% { border-color: #cbd5e1; box-shadow: 0 4px 16px rgba(15,23,42,0.06); background-color: #fff; }
  15%, 70% { border-color: #0b3a66; box-shadow: 0 0 0 calc(var(--nq-u) * 0.35) rgba(11,58,102,0.35), 0 4px 24px rgba(15,23,42,0.15); background-color: #e8f0fa; }
}
@keyframes nq-queue-pop {
  0% { transform: scale(1); }
  20% { transform: scale(1.14); }
  45% { transform: scale(1); }
}
.nq-board .nq-card[data-flash="1"] { animation: nq-card-flash 3s ease-in-out 1; background-image: none; }
.nq-board .nq-card[data-flash="1"] .nq-queue { animation: nq-queue-pop 1.2s ease-out 1; }
@media (prefers-reduced-motion: reduce) {
  .nq-board .nq-card[data-flash="1"] .nq-queue { animation: none; }
}

/* Side-video stacked column: cards are short and wide, so cap the number. */
.nq-board[data-stack="1"] { --nq-u: clamp(9px, 1.4vmin, 14px); }
.nq-board[data-stack="1"] .nq-card { padding: calc(var(--nq-u) * 0.8) calc(var(--nq-u) * 1.2); }
.nq-board[data-stack="1"] .nq-room { font-size: calc(var(--nq-u) * 2.4); }
.nq-board[data-stack="1"] .nq-doctor { font-size: calc(var(--nq-u) * 1.4); }
.nq-board[data-stack="1"] .nq-queue { font-size: calc(var(--nq-u) * 6.5); }
`;

export function RoomCardsGrid({
  clinicName,
  rooms,
  fixPagination = false,
  sessionName = "",
}: RoomCardsGridProps) {
  const sideVideo = useSideVideoLayout();
  const multiRoomColumnStack = sideVideo && rooms.length > 1;

  const pageSize = fixPagination ? QUEUE_BOARD_MAX_PER_PAGE : rooms.length || 1;
  const pageCount = Math.max(1, Math.ceil(rooms.length / pageSize));
  const [pageIndex, setPageIndex] = useState(0);

  useEffect(() => {
    setPageIndex((prev) => (prev >= pageCount ? 0 : prev));
  }, [pageCount, rooms.length]);

  useEffect(() => {
    if (!fixPagination || pageCount <= 1) return;
    const id = window.setInterval(() => {
      setPageIndex((prev) => (prev + 1) % pageCount);
    }, PAGE_ROTATE_MS);
    return () => window.clearInterval(id);
  }, [fixPagination, pageCount]);

  const visibleRooms = useMemo(() => {
    if (!fixPagination || rooms.length <= QUEUE_BOARD_MAX_PER_PAGE) return rooms;
    const start = pageIndex * QUEUE_BOARD_MAX_PER_PAGE;
    return rooms.slice(start, start + QUEUE_BOARD_MAX_PER_PAGE);
  }, [fixPagination, rooms, pageIndex]);

  const scaleCount = visibleRooms.length;
  const denseHeader = scaleCount > 4;
  const sharedTitle = sessionName.trim();
  const gapClass =
    scaleCount <= 2
      ? "gap-[clamp(16px,2.5vmin,32px)]"
      : scaleCount <= 4
        ? "gap-[clamp(12px,1.8vmin,24px)]"
        : scaleCount <= 6
          ? "gap-[clamp(10px,1.5vmin,20px)]"
          : "gap-[clamp(8px,1.2vmin,16px)]";

  return (
    <div
      className={[
        queueFont,
        "nq-board relative box-border flex w-full max-w-[100vw] select-none flex-col overflow-hidden bg-white antialiased",
        multiRoomColumnStack
          ? "h-full min-h-0 flex-1"
          : "h-[100dvh] max-h-[100dvh] min-h-0",
      ].join(" ")}
      data-count={scaleCount}
      data-stack={multiRoomColumnStack ? "1" : "0"}
    >
      <style>{QUEUE_BOARD_COUNT_CSS}</style>

      {!multiRoomColumnStack ? <CareCornerLogo variant="grid" /> : null}

      <WelcomeHeader
        clinicName={clinicName}
        layout="inline"
        compact={multiRoomColumnStack || denseHeader}
        sessionLabel={sharedTitle}
      />

      <div
        className={[
          "flex min-h-0 w-full flex-1 flex-col overflow-hidden",
          multiRoomColumnStack
            ? "items-stretch px-3 pb-3 pt-1"
            : "items-center justify-center px-[clamp(16px,2.5vmin,48px)] pb-[clamp(56px,7vmin,96px)] pt-1",
        ].join(" ")}
      >
        {rooms.length === 0 ? (
          <div
            className={`${queueFont} max-w-[520px] self-center p-[clamp(24px,4vmin,48px)] text-center text-lg leading-normal text-[#334155]`}
          >
            No rooms are configured for this clinic yet. Add rooms in Settings (Manage time → Rooms), then
            refresh this page.
          </div>
        ) : multiRoomColumnStack ? (
          <div className="flex h-full min-h-0 w-full flex-1 flex-col gap-2">
            {visibleRooms.map((room) => (
              <RoomCard key={room.id} room={room} hideTitle={!!sharedTitle} />
            ))}
            {fixPagination && pageCount > 1 ? (
              <div className="shrink-0 pt-1 text-center text-[10px] font-semibold tracking-wide text-slate-400">
                {pageIndex + 1} / {pageCount}
              </div>
            ) : null}
          </div>
        ) : (
          <div className="nq-grid-shell flex flex-col gap-2">
            <div
              className={[
                "grid h-full min-h-0 w-full flex-1 auto-rows-fr",
                gridColsClass(scaleCount, fixPagination),
                gapClass,
              ].join(" ")}
            >
              {visibleRooms.map((room) => (
                <RoomCard key={room.id} room={room} hideTitle={!!sharedTitle} />
              ))}
            </div>
            {fixPagination && pageCount > 1 ? (
              <div className="shrink-0 text-center text-sm font-semibold tracking-wide text-slate-400">
                Page {pageIndex + 1} of {pageCount}
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}
